import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import { apiJson } from "../utils/api";
import useSeo from "../hooks/useSeo";

function LevelBadge({ level }) {
  const map = {
    较高: "bg-emerald-50 text-emerald-800 border-emerald-200",
    中等: "bg-amber-50 text-amber-900 border-amber-200",
    较低: "bg-slate-50 text-slate-700 border-slate-200",
  };
  return (
    <span
      className={`text-[11px] px-2 py-0.5 rounded-full border ${
        map[level] || map["中等"]
      }`}
    >
      {level || "中等"}
    </span>
  );
}

function DimBar({ score }) {
  const pct = Math.round(Math.min(100, Math.max(8, (Number(score) || 0.5) * 100)));
  return (
    <div className="h-1.5 rounded-full bg-violet-100 overflow-hidden">
      <div
        className="h-full rounded-full bg-gradient-to-r from-amber-400 to-violet-500"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export default function BehaviorProfilePage() {
  const navigate = useNavigate();
  const userId = Number(localStorage.getItem("user_id"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useSeo({
    title: "我的关系行为画像｜沟通方式与关系成长",
    metaDescription:
      "根据授权后的陪伴互动，逐步形成你的关系行为观察：沟通、共情、确认需求与冲突处理倾向。不是心理诊断。",
    path: "/ai/behavior-profile",
  });

  useEffect(() => {
    if (!userId) {
      navigate(
        `/login?redirect=${encodeURIComponent("/ai/behavior-profile")}`,
        { replace: true }
      );
    }
  }, [userId, navigate]);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError("");
    try {
      const { res, data: body } = await apiJson(
        `/api/ai/behavior-profile?user_id=${userId}`
      );
      if (!res.ok) throw new Error(body?.detail || "加载失败");
      setData(body);
    } catch (err) {
      setError(err.message || "加载失败");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const setConsent = async (allowed) => {
    setBusy(true);
    try {
      const { res, data: body } = await apiJson("/api/ai/companion/consent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, allowed }),
      });
      if (!res.ok) throw new Error(body?.detail || "保存失败");
      await load();
    } catch (err) {
      alert(err.message || "保存失败");
    } finally {
      setBusy(false);
    }
  };

  const summary = data?.summary || {};
  const consent = data?.consent || {};

  return (
    <div className="min-h-screen bg-[#fbf7f2] flex flex-col">
      <Header />
      <main className="flex-1 w-full mx-auto px-4 py-8 max-w-5xl space-y-6">
        <section className="rounded-3xl bg-gradient-to-br from-amber-50 via-violet-50 to-rose-50 border border-white p-6 md:p-8 space-y-3">
          <p className="text-sm text-violet-600 font-medium">关系行为观察</p>
          <h1 className="text-3xl font-extrabold text-gray-900">
            我的关系行为画像
          </h1>
          <p className="text-sm text-gray-600 max-w-2xl leading-relaxed">
            不是人格诊断，而是根据你授权后的陪伴互动，逐渐看到的沟通与关系倾向。
            样本会累积，观察也会慢慢更清晰。
          </p>
          <div className="flex flex-wrap gap-2 text-xs pt-1">
            <Link to="/ai/consultant?tab=chat" className="text-amber-800 hover:underline">
              和我聊聊
            </Link>
            <span className="text-gray-300">·</span>
            <Link to="/ai/simulation" className="text-violet-700 hover:underline">
              关系模拟
            </Link>
            <span className="text-gray-300">·</span>
            <Link
              to="/ai/relationship-map"
              className="text-violet-700 hover:underline"
            >
              AI关系地图
            </Link>
            <span className="text-gray-300">·</span>
            <Link
              to="/ai/relationship-growth"
              className="text-violet-700 hover:underline"
            >
              成长追踪
            </Link>
            <span className="text-gray-300">·</span>
            <Link
              to="/ai/relationship-memory"
              className="text-violet-700 hover:underline"
            >
              关系记忆看板
            </Link>
          </div>
        </section>

        {error && (
          <div className="bg-red-50 border border-red-100 text-red-700 text-sm rounded-xl px-4 py-3">
            {error}
          </div>
        )}

        {loading ? (
          <p className="text-sm text-gray-400">加载中…</p>
        ) : (
          <div className="space-y-5 animate-[fadeIn_0.3s_ease]">
            <section className="bg-white rounded-2xl border p-5 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-semibold text-gray-800">数据授权</h2>
                <span className="text-xs text-gray-400">
                  样本 {data?.sample_count || 0} · 特征条数{" "}
                  {data?.feature_count || 0}
                  {data?.updated_at
                    ? ` · 更新 ${String(data.updated_at).slice(0, 16).replace("T", " ")}`
                    : ""}
                </span>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed">
                {consent.copy ||
                  "授权后，我们只会提取结构化关系行为特征，用于改善你自己的关系模拟与关系地图，不会把聊天原文交给模拟或他人。"}
              </p>
              {(consent.details || []).length > 0 && (
                <ul className="text-[11px] text-gray-500 space-y-1 list-disc pl-4">
                  {consent.details.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              )}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={busy || consent.allowed}
                  onClick={() => setConsent(true)}
                  className="px-4 py-2 rounded-full bg-violet-600 text-white text-xs disabled:opacity-50"
                >
                  {consent.allowed ? "已允许用于关系分析" : "允许用于我的关系分析"}
                </button>
                <button
                  type="button"
                  disabled={busy || (!consent.allowed && consent.prompted)}
                  onClick={() => setConsent(false)}
                  className="px-4 py-2 rounded-full border text-xs disabled:opacity-50"
                >
                  {consent.allowed ? "关闭行为分析" : "暂时不使用"}
                </button>
                <Link
                  to="/ai/relationship-map"
                  className="px-4 py-2 rounded-full border border-violet-200 text-violet-700 text-xs"
                >
                  查看/修正关系地图
                </Link>
              </div>
              {!consent.allowed && (
                <p className="text-[11px] text-amber-700">
                  当前未授权：陪聊仍可用，但画像不会更新，关系模拟也不会读取行为特征。
                </p>
              )}
              {consent.allowed && (
                <p className="text-[11px] text-emerald-700">
                  已授权：结构化观察会写入行为画像与关系地图第 4/5 层，可随时修正或关闭。
                </p>
              )}
            </section>

            <section className="bg-white rounded-2xl border p-5 space-y-3">
              <h2 className="font-semibold text-gray-800">总览</h2>
              <p className="text-sm text-gray-700 leading-relaxed">
                {summary.headline}
              </p>
              {!data?.exists && (
                <p className="text-xs text-violet-600 bg-violet-50 rounded-xl px-3 py-2">
                  建议：打开「和我聊聊」，自然聊几轮并授权，画像会开始累积。
                </p>
              )}
            </section>

            <section className="bg-white rounded-2xl border p-5 space-y-4">
              <h2 className="font-semibold text-gray-800">十个关系行为维度</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {(data?.dims || []).map((d) => (
                  <div
                    key={d.key}
                    className="rounded-xl border border-violet-50 bg-violet-50/20 px-3 py-3 space-y-1.5"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-gray-800">
                        {d.label}
                      </span>
                      <LevelBadge level={d.level} />
                    </div>
                    <DimBar score={d.score} />
                    {d.why && (
                      <p className="text-[11px] text-gray-500 leading-relaxed">
                        {d.why}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </section>

            <div className="grid sm:grid-cols-2 gap-4">
              <section className="bg-emerald-50/70 rounded-2xl border border-emerald-100 p-4 space-y-2">
                <h3 className="font-semibold text-emerald-900 text-sm">我的优势</h3>
                {(summary.strengths || []).length === 0 ? (
                  <p className="text-xs text-emerald-800/70">样本累积后会出现。</p>
                ) : (
                  <ul className="text-xs text-emerald-950 space-y-1.5">
                    {summary.strengths.map((s) => (
                      <li key={s}>· {s}</li>
                    ))}
                  </ul>
                )}
              </section>
              <section className="bg-amber-50/70 rounded-2xl border border-amber-100 p-4 space-y-2">
                <h3 className="font-semibold text-amber-900 text-sm">关系挑战</h3>
                {(summary.challenges || []).length === 0 ? (
                  <p className="text-xs text-amber-800/70">样本累积后会出现。</p>
                ) : (
                  <ul className="text-xs text-amber-950 space-y-1.5">
                    {summary.challenges.map((s) => (
                      <li key={s}>· {s}</li>
                    ))}
                  </ul>
                )}
              </section>
            </div>

            {(summary.patterns || []).length > 0 && (
              <section className="bg-white rounded-2xl border p-4 space-y-2">
                <h3 className="font-semibold text-gray-800 text-sm">
                  从近期互动来看的模式
                </h3>
                <ul className="text-sm text-gray-700 space-y-1.5">
                  {summary.patterns.map((p) => (
                    <li key={p}>· {p}</li>
                  ))}
                </ul>
              </section>
            )}

            {((data?.stable_insights || summary.stable_insights || []).length > 0 ||
              (data?.sensitive_zones || summary.sensitive_zones || []).length > 0) && (
              <div className="grid sm:grid-cols-2 gap-4">
                <section className="bg-white rounded-2xl border p-4 space-y-2">
                  <h3 className="font-semibold text-gray-800 text-sm">稳定观察</h3>
                  <p className="text-[11px] text-gray-400">会回写到关系地图第四层</p>
                  <ul className="text-xs text-gray-700 space-y-1.5">
                    {(data?.stable_insights || summary.stable_insights || []).map((s) => (
                      <li key={s}>· {s}</li>
                    ))}
                  </ul>
                </section>
                <section className="bg-rose-50/60 rounded-2xl border border-rose-100 p-4 space-y-2">
                  <h3 className="font-semibold text-rose-900 text-sm">敏感议题</h3>
                  <p className="text-[11px] text-rose-800/70">会回写到关系地图第五层（概括标签，非原文）</p>
                  <ul className="text-xs text-rose-950 space-y-1.5">
                    {(data?.sensitive_zones || summary.sensitive_zones || []).map((s) => (
                      <li key={s}>· {s}</li>
                    ))}
                  </ul>
                </section>
              </div>
            )}

            <section className="bg-violet-50/60 rounded-2xl border border-violet-100 p-4 space-y-2">
              <h3 className="font-semibold text-violet-900 text-sm">成长方向</h3>
              <p className="text-sm text-violet-950 leading-relaxed">
                {summary.growth}
              </p>
            </section>

            <section className="flex flex-wrap gap-2">
              {(data?.next_steps || []).map((s) => (
                <Link
                  key={s.path}
                  to={s.path}
                  className="px-4 py-2.5 rounded-full bg-violet-600 text-white text-sm"
                >
                  {s.label}
                </Link>
              ))}
              <Link
                to="/ai/relationship-train"
                className="px-4 py-2.5 rounded-full bg-emerald-600 text-white text-sm"
              >
                开始关系训练
              </Link>
              <Link
                to="/ai/consultant"
                className="px-4 py-2.5 rounded-full border text-sm text-gray-600"
              >
                返回 AI 咨询
              </Link>
            </section>

            <p className="text-[11px] text-gray-400 leading-relaxed">
              {data?.disclaimer}
            </p>
          </div>
        )}
      </main>
      <Footer />
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
